import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface Predictability {
  averageHitRate: number | null;
  varianceOfHitRate: number | null;
  sampleSize: number;
  top3Concentration: number;
  top3Value: number;
  pipelineTotal: number;
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * Story 57.6 — predictability of the business. Hit rate (won-since vs
 * commit) over the last several snapshots + concentration risk
 * (top-3 deals as % of open pipeline). High concentration means one
 * loss can move the quarter.
 */
export function PredictabilityWidget() {
  const [data, setData] = useState<Predictability | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<Predictability>('/analytics/predictability')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading predictability…
      </div>
    );
  }
  if (!data) return null;

  const hitRate = data.averageHitRate !== null ? Number(data.averageHitRate) : null;
  const variance = data.varianceOfHitRate !== null ? Number(data.varianceOfHitRate) : null;
  const concentration = Number(data.top3Concentration);
  const concentrationHigh = concentration >= 0.5;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Predictability
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs uppercase text-muted-foreground">Forecast hit rate</div>
          {hitRate !== null ? (
            <>
              <div className="mt-1 text-2xl font-bold">{(hitRate * 100).toFixed(0)}%</div>
              <div className="text-xs text-muted-foreground">
                avg over {data.sampleSize} snapshots
                {variance !== null
                  ? ` · σ²=${(variance * 10000).toFixed(0)} bps²`
                  : ''}
              </div>
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">
              Need more snapshot history.
            </div>
          )}
        </div>
        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs uppercase text-muted-foreground">
            Concentration risk (top 3 deals)
          </div>
          <div
            className={`mt-1 text-2xl font-bold ${
              concentrationHigh ? 'text-rose-300' : ''
            }`}
          >
            {(concentration * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-muted-foreground">
            {fmtCompact(data.top3Value)} of {fmtCompact(data.pipelineTotal)} open
            {concentrationHigh && ' · single deals can move the quarter'}
          </div>
        </div>
      </div>
    </div>
  );
}
